class MultistepAnswers extends HTMLElement {
  constructor() {
    super()
    const dependencies = ['int-local-format', 'dec-local-format', 'phone-with-prefix']
    this.onceReady = Promise.all(dependencies.map((element) => customElements.whenDefined(element)))
    this.getValue = () => undefined
  }

  connectedCallback() {
    // Autofocus al navegar entre pasos (ENH-RNK-2850). El atributo HTML
    // `autofocus` no sirve aquí: los pasos viven todos en el DOM desde el render
    // inicial y solo se muestran/ocultan con CSS, así que el navegador lo
    // ignoraría tras la carga del documento.
    this.addEventListener('show', (event) => {
      if (event.detail?.focus) this._focusControl()
    })

    this.onceReady.then(() => {
      switch (this.getAttribute('view')) {
        case 'email':
          this._setupNormalInput('input[type=email]')
          break
        case 'numerico':
          this._setupNumericInput('int-local-format')
          break
        case 'decimal':
          this._setupNumericInput('dec-local-format')
          break
        case 'opcion_multiple':
          this._setupOpcionMultiple()
          break
        case 'opcion_simple':
          this._setupOpcionSimple()
          break
        case 'select_provincia':
          this._setupSelect()
          break
        case 'si_no':
          this._setupSiNo()
          break
        case 'telefono':
          this._setupTelefono()
          break
        case 'texto':
          this._setupNormalInput('input[type=text]')
          break
        case 'select_ciudad':
          this._setupSelect()
          break
        case 'codigo_postal':
          this._setupNormalInput('input[type=text]')
          break
        case 'email_con_aceptacion':
          this._setupEmailWithAcceptance()
          break
        case 'email_con_aceptaciones':
          this._setupEmailWithAcceptances()
          break
        case 'email_con_acuse_cesion':
          this._setupEmailConAcuseYCesion()
          break
        // TC-124-2.2.11.17: brokers ES (email_con_acuse_finalidad_y_newsletter) reutiliza el
        // mismo setup que préstamos. En brokers, "marcar todas" coordina acuse + finalidad +
        // la newsletter de bolsa (todas tratamientos de 1ª parte); el cableado vive en
        // _setupEmailConAcuse, guardado por `if (newsletter)` porque préstamos no la tiene.
        case 'email_con_acuse_finalidad':
        case 'email_con_acuse_finalidad_y_newsletter':
          this._setupEmailConAcuse()
          break
        case 'telefono_con_aceptaciones':
          this._setupPhoneWithAcceptances()
          break
        case 'documento_id':
          this._setupNormalInput('input[type=text]')
          break
        case 'fecha':
          this._setupDateInput('input[type=date]')
          break
      }
    })
  }

  _commonTextInputSetup(input) {
    const isValid = input.validity.valid
    this._emitValidity(isValid)

    input.addEventListener('input', () => {
      this._emitValidity(input.validity.valid)
    })
    this.addEventListener('show', function () {
      input.required = true
      this._emitValidity(input.validity.valid)
    })
    this.addEventListener('hide', function () {
      input.required = false
    })
    this._setKeyboardEvents(input, {}, false)

    this.getValue = () => input.value
  }

  _setupDateInput(selector) {
    const dateInput = this.querySelector(selector);

    if (dateInput) {
      const handleInputChange = () => {
        this._emitValidity(dateInput.value !== '');
      };

      dateInput.addEventListener('change', handleInputChange);
      dateInput.addEventListener('input', handleInputChange);

      this.addEventListener('show', () => {
        dateInput.required = true;
        handleInputChange();
      });

      this.addEventListener('hide', () => {
        dateInput.required = false;
        dateInput.value = '';
      });

      this.getValue = () => dateInput.value;
    }
  }

  _setupNumericInput(componentName) {
    const input = this.querySelector(`input[is=${componentName}]`)
    this._commonTextInputSetup(input)

    input.form.addEventListener('submit', () => {
      input.removeAttribute('pattern')
      input.value = input.originalValue()
    })
  }

  _setupNormalInput(selector) {
    const input = this.querySelector(selector)
    this._commonTextInputSetup(input)
  }

  _setupOpcionMultiple() {
    const limit = parseInt(this.getAttribute('limit'))
    const checkboxes = this.querySelectorAll('input[type="checkbox"]')
    const checkeds = Array.from(checkboxes).filter((checkbox) => checkbox.checked)
    const checkedsSize = checkeds.length
    const isValid = 0 < checkedsSize <= limit

    this.getValue = () => {
      const currentlyChecked = this.querySelectorAll('input[type="checkbox"]:checked')

      return Array.from(currentlyChecked).map(input => input.value).join(',')
    }

    this._emitValidity(isValid)

    checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => {
        const checkedBoxes = this.querySelectorAll('input:checked')
        if (checkedBoxes.length > 0) {
          this._emitValidity(true)
          if (checkedBoxes.length > limit) {
            checkbox.checked = false
          }
        } else {
          this._emitValidity(false)
        }
      })
    })
  }

  _setupOpcionSimple() {
    const radios = this.querySelectorAll('input[type="radio"]')
    const selectedRadio = Array.from(radios).find((radio) => radio.checked)

    this.getValue = () => this.querySelector('input[type="radio"]:checked').value

    if (selectedRadio) {
      this._emitValidity(true, { go: selectedRadio.getAttribute('go') })
    }

    radios.forEach(radio => {
      radio.addEventListener('change', () => {
        if (radio.checked) {
          this._emitValidity(true, { go: radio.getAttribute('go'), auto: true })
        }
      })
    })
  }

  _setupSelect() {
    const selectProvincia = this.querySelector('select')
    const isValid = selectProvincia.selectedIndex > 0

    this.getValue = () => selectProvincia.options[selectProvincia.selectedIndex].value

    this._emitValidity(isValid)

    selectProvincia.addEventListener('change', () => {
      const isValid = selectProvincia.selectedIndex > 0
      this._emitValidity(isValid, { auto: true })
    })
  }

  _setupSiNo() {
    const siNo = this.querySelector('input[type=checkbox]')
    const isValid = siNo.checked

    this.getValue = () => siNo.checked

    this._emitValidity(isValid)

    siNo.addEventListener('change', (event) => {
      const isValid = event.target.checked
      this._emitValidity(isValid)
    })
  }

  _setupTelefono() {
    const phoneComponent = this.querySelector('phone-with-prefix')
    const phoneInput = phoneComponent.querySelector('input[type=tel]')
    const isValid = () => { return phoneInput.value.length > 0 && phoneInput.validity.valid }

    this.getValue = () => phoneComponent.inputTelefonoCompleto.value

    this._emitValidity(isValid())

    // TODO: track this listeners, both are triggered twice...
    // review `this.esValido = function () {...}`
    // at `app/cells/telephone_field/js.haml:27`
    phoneComponent.addEventListener('phone-is-valid', () => {
      // replaced `true` value with a function, but still extrange behaviour
      this._emitValidity(isValid())
    })
    phoneComponent.addEventListener('phone-is-invalid', () => {
      this._emitValidity(false)
    })

    this._setKeyboardEvents(phoneInput)
  }

  _setupEmailWithAcceptance() {
    const emailInput = this.querySelector('input[type=email]')
    const acceptance = this.querySelector('input[type=checkbox]')
    const isValid = () => { return emailInput.validity.valid && acceptance.checked }

    this.getValue = () => emailInput.value

    this._emitValidity(isValid())

    emailInput.addEventListener('input', () => {
      this._emitValidity(isValid())
    })

    acceptance.addEventListener('change', () => {
      this._emitValidity(isValid())
    })
  }

  _setupEmailWithAcceptances() {
    const emailInput = this.querySelector('input[type=email]')
    const acceptance = this.querySelector('input[type=checkbox][id=captador_simulacion_acepta_terminos]')
    const acceptanceReceiveCommercialCommunications = this.querySelector('input[type=checkbox][id=captador_simulacion_acepto_recibir_comunicaciones_comerciales]')
    const checkAllOptions = this.querySelector('input[type=checkbox][id=captador_simulacion_marcar_todas_las_opciones]')

    const isValid = () => { return emailInput.validity.valid && acceptanceReceiveCommercialCommunications.checked }

    this.getValue = () => emailInput.value

    this._emitValidity(isValid())

    emailInput.addEventListener('input', () => {
      this._emitValidity(isValid())
    })

    acceptance.addEventListener('change', () => {
      this._emitValidity(isValid())
      checkAllOptions.checked = false

      let acceptanceCesionATerceros = this.querySelectorAll('input[type=checkbox][name="captador_simulacion[clientes][]"]')

      if (acceptance.checked === false) {
        acceptanceCesionATerceros.forEach((sector) => {
          sector.checked = false
        })
      } else {
        acceptanceCesionATerceros.forEach((sector) => {
          sector.checked = true
        })
      }
    })

    acceptanceReceiveCommercialCommunications.addEventListener('change', () => {
      this._emitValidity(isValid())
      checkAllOptions.checked = false
    })

    checkAllOptions.addEventListener('change', () => {
      let clientes = document.querySelectorAll('input[type=checkbox][name="captador_simulacion[clientes][]"]')

      let clientConsent = !!checkAllOptions.checked
      acceptanceReceiveCommercialCommunications.checked = clientConsent
      acceptance.checked = clientConsent
      clientes.forEach((cliente) => {
        cliente.checked = clientConsent
      })

      setTimeout(() => {
        this._emitValidity(isValid())
      }, 500)
    })
  }

  // v2 (TC-124-2.2.11.12): asistente de depósitos (Vía B). La validez del paso exige
  // el ACUSE del aviso legal (obligatorio) + el email. La finalidad (doble efecto) y la
  // cesión a terceros (acepta_terminos + clientes[], solo si hay terceros) son
  // OPCIONALES. "Marcar todas" coordina acuse + finalidad + cesión + clientes. Ningún
  // checkbox premarcado.
  _setupEmailConAcuseYCesion() {
    const emailInput = this.querySelector('input[type=email]')
    const acuse = this.querySelector('input[type=checkbox][id=captador_simulacion_acepta_aviso_legal]')

    this.getValue = () => emailInput.value

    // Sin acuse (no debería ocurrir): validez solo por email, sin coordinación.
    if (!acuse) {
      const soloEmail = () => { return emailInput.validity.valid }
      this._emitValidity(soloEmail())
      emailInput.addEventListener('input', () => { this._emitValidity(soloEmail()) })
      return
    }

    const finalidad = this.querySelector('input[type=checkbox][id=captador_simulacion_consent_finalidad_aceptado]')
    const cesion = this.querySelector('input[type=checkbox][id=captador_simulacion_acepta_terminos]')
    const checkAllOptions = this.querySelector('input[type=checkbox][id=captador_simulacion_marcar_todas_las_opciones]')
    const clientes = () => document.querySelectorAll('input[type=checkbox][name="captador_simulacion[clientes][]"]')

    const isValid = () => { return emailInput.validity.valid && acuse.checked }

    this._emitValidity(isValid())

    emailInput.addEventListener('input', () => {
      this._emitValidity(isValid())
    })

    acuse.addEventListener('change', () => {
      this._emitValidity(isValid())
      if (!acuse.checked && checkAllOptions) {
        checkAllOptions.checked = false
      }
    })

    if (finalidad) {
      finalidad.addEventListener('change', () => {
        if (!finalidad.checked && checkAllOptions) {
          checkAllOptions.checked = false
        }
      })
    }

    // Cesión a terceros: FUERA de "marcar todas" (spec §2.4 — tratamiento de mayor
    // riesgo, acto separado y deliberado). El check agregado de cesión solo coordina
    // sus propios clientes (granularidad a un clic); ni marca/desmarca el maestro ni
    // se ve afectado por él.
    if (cesion) {
      cesion.addEventListener('change', () => {
        clientes().forEach((cliente) => { cliente.checked = cesion.checked })
      })
    }

    if (checkAllOptions) {
      checkAllOptions.addEventListener('change', () => {
        const marcar = !!checkAllOptions.checked
        acuse.checked = marcar
        if (finalidad) { finalidad.checked = marcar }
        this._emitValidity(isValid())
      })
    }
  }

  // v2 (TC-124-2.2.11.15): asistentes de préstamos y brokers ES (TC-124-2.2.11.17). La
  // validez del paso exige el ACUSE del aviso legal (obligatorio) + el email; la finalidad
  // es OPCIONAL. "Marcar todas" coordina acuse + finalidad (y, en brokers, la newsletter de
  // bolsa: tratamiento de 1ª parte, mismo nivel de riesgo que la finalidad). Ningún
  // checkbox premarcado.
  _setupEmailConAcuse() {
    const emailInput = this.querySelector('input[type=email]')
    const acuse = this.querySelector('input[type=checkbox][id=captador_simulacion_acepta_aviso_legal]')

    this.getValue = () => emailInput.value

    // Sin acuse (no debería ocurrir): validez solo por email, sin coordinación.
    if (!acuse) {
      const soloEmail = () => { return emailInput.validity.valid }
      this._emitValidity(soloEmail())
      emailInput.addEventListener('input', () => { this._emitValidity(soloEmail()) })
      return
    }

    const finalidad = this.querySelector('input[type=checkbox][id=captador_simulacion_consent_finalidad_aceptado]')
    // Newsletter de bolsa: solo presente en brokers (ausente en préstamos), por eso todo su
    // cableado va guardado por `if (newsletter)`. Es finalidad de 1ª parte como la de
    // marketing, así que "marcar todas" la coordina igual (TC-124-2.2.11.17).
    const newsletter = this.querySelector('input[type=checkbox][id=captador_simulacion_suscribir_newsletter]')
    const checkAllOptions = this.querySelector('input[type=checkbox][id=captador_simulacion_marcar_todas_las_opciones]')

    const isValid = () => { return emailInput.validity.valid && acuse.checked }

    this._emitValidity(isValid())

    emailInput.addEventListener('input', () => {
      this._emitValidity(isValid())
    })

    acuse.addEventListener('change', () => {
      this._emitValidity(isValid())
      if (!acuse.checked && checkAllOptions) {
        checkAllOptions.checked = false
      }
    })

    if (finalidad) {
      finalidad.addEventListener('change', () => {
        if (!finalidad.checked && checkAllOptions) {
          checkAllOptions.checked = false
        }
      })
    }

    if (newsletter) {
      newsletter.addEventListener('change', () => {
        if (!newsletter.checked && checkAllOptions) {
          checkAllOptions.checked = false
        }
      })
    }

    if (checkAllOptions) {
      checkAllOptions.addEventListener('change', () => {
        const marcar = !!checkAllOptions.checked
        acuse.checked = marcar
        if (finalidad) {
          finalidad.checked = marcar
        }
        if (newsletter) {
          newsletter.checked = marcar
        }
        this._emitValidity(isValid())
      })
    }
  }

  // TC-124-2.2.11.3 / .10: asistente de hipotecas, paso de teléfono. La validez del paso
  // exige el teléfono (obligatorio: es contractual, el contacto es la finalidad del
  // asistente) Y el ACUSE OBLIGATORIO del aviso legal / condiciones de uso. El acuse es
  // un acuse de información (RGPD art. 13/14), no un consentimiento de tratamiento, por
  // eso SÍ puede gatear el avance del wizard (a diferencia de la finalidad, que antes lo
  // bloqueaba y por eso era un consentimiento inválido — RGPD art. 4.11/7). La finalidad
  // (comunicaciones comerciales), WhatsApp y la cesión a terceros siguen OPCIONALES.
  // "Marcar todas las opciones" coordina el acuse, la finalidad y WhatsApp (acuse de
  // información + tratamientos de 1ª parte), pero NO la cesión a terceros: esta queda
  // fuera del maestro porque es un tratamiento de mayor riesgo que exige un acto separado
  // y deliberado (RGPD, consentimiento específico), igual que en brokers/depósitos.
  // Ningún checkbox premarcado.
  _setupPhoneWithAcceptances() {
    const phoneComponent = this.querySelector('phone-with-prefix')
    const phoneInput = phoneComponent.querySelector('input[type=tel]')
    const acuse = this.querySelector('input[type=checkbox][id=captador_simulacion_acepta_aviso_legal]')
    const acceptance = this.querySelector('input[type=checkbox][id=captador_simulacion_acepta_terminos]')
    const checkAllOptions = this.querySelector('input[type=checkbox][id=captador_simulacion_marcar_todas_las_opciones]')
    const acceptanceReceiveCommercialCommunications = this.querySelector('input[type=checkbox][id=captador_simulacion_acepto_recibir_comunicaciones_comerciales]')
    const acceptanceWhatsApp = this.querySelector('input[type=checkbox][id=captador_simulacion_acepto_comunicaciones_whatsapp]')
    // El acuse del aviso legal es obligatorio: sin él, el paso no es válido y el wizard no
    // avanza. El guard `!acuse` mantiene la robustez si por algún motivo no se renderizara.
    const isValid = () => { return phoneInput.value.length > 0 && phoneInput.validity.valid && (!acuse || acuse.checked) }

    this.getValue = () => phoneComponent.inputTelefonoCompleto.value

    this._emitValidity(isValid())

    phoneComponent.addEventListener('phone-is-valid', () => {
      this._emitValidity(isValid())
    })
    phoneComponent.addEventListener('phone-is-invalid', () => {
      this._emitValidity(false)
    })
    if (acuse) {
      acuse.addEventListener('change', () => {
        this._emitValidity(isValid())
        if (!acuse.checked) {
          checkAllOptions.checked = false
        }
      })
    }
    // Cesión a terceros: FUERA de "marcar todas". El checkbox agregado de cesión solo
    // coordina sus propios clientes (granularidad a un clic); ni se ve afectado por el
    // maestro ni lo marca/desmarca.
    acceptance.addEventListener('change', () => {
      const clientes = document.querySelectorAll('input[type=checkbox][name="captador_simulacion[clientes][]"]')
      clientes.forEach((cliente) => { cliente.checked = acceptance.checked })
    })
    acceptanceReceiveCommercialCommunications.addEventListener('change', () => {
      this._emitValidity(isValid())
      checkAllOptions.checked = false
    })
    if (acceptanceWhatsApp) {
      acceptanceWhatsApp.addEventListener('change', () => {
        this._emitValidity(isValid())
        checkAllOptions.checked = false
      })
    }
    checkAllOptions.addEventListener('change', () => {
      if (acuse) {
        acuse.checked = checkAllOptions.checked
      }
      acceptanceReceiveCommercialCommunications.checked = checkAllOptions.checked
      if (acceptanceWhatsApp) {
        acceptanceWhatsApp.checked = checkAllOptions.checked
      }

      setTimeout(() => {
        this._emitValidity(isValid())
      }, 500)
    })
    this._setKeyboardEvents(phoneInput)
  }

  // Enfoca el control principal de respuesta: el primer campo en orden de
  // documento (en email_con_aceptaciones, p. ej., el email precede a los
  // checkboxes).
  //
  // NO se enfocan las vistas de opciones (opcion_simple/opcion_multiple): el
  // anillo de foco sobre el primer radio/checkbox parece una opción
  // preseleccionada o sugerida, y no lo es — sesga la respuesta del usuario en un
  // asistente de captación. El autofocus solo aporta valor donde la acción es
  // escribir.
  _focusControl() {
    if (['opcion_simple', 'opcion_multiple'].includes(this.getAttribute('view'))) return

    const control = this.querySelector('input:not([type=hidden]):not([disabled]), select, textarea')
    control?.focus()
  }

  _setKeyboardEvents(input) {
    input.addEventListener('keydown', (inputEvent) => {
      const keyCode = inputEvent.keyCode
      const isTab = keyCode === 9
      const isEnter = keyCode === 13
      if ((isEnter || isTab) && inputEvent.target.checkValidity()) {
        inputEvent.preventDefault()
        this._emitValidity(true, { auto: true })
      }
    })
  }

  _emitValidity(state, detail = {}) {
    const name = state ? 'valid' : 'invalid'
    const event = new CustomEvent(name, { bubbles: true, detail: detail })

    this.dispatchEvent(event)
  }

  get value() {
    return this.getValue()
  }
}

export default MultistepAnswers
